/****************
 * PostHog on the web. Rules and masking: posthog.core.ts.
 * Started once from main.tsx; identify() follows sign-in; capture() is fed by
 * analytics.ts track() so both pipelines see the same event names.
 ****************/
import { fetchPostHogConfig, posthogOptions, POSTHOG_OPT_OUT_KEY } from './posthog.core';

type PH = typeof import('posthog-js').default;
let ph: PH | null = null;
let pendingId: string | null = null;
let started = false;

function optedOut(): boolean {
  try { return localStorage.getItem(POSTHOG_OPT_OUT_KEY) === '1'; } catch { return false; }
}

export async function startPostHog(): Promise<void> {
  if (started || optedOut()) return;
  started = true;
  try {
    const cfg = await fetchPostHogConfig(import.meta.env.VITE_API_URL || '');
    if (!cfg) return;
    const mod = await import('posthog-js');
    mod.default.init(cfg.key, posthogOptions(cfg));
    ph = mod.default;
    if (pendingId) ph.identify(pendingId);
  } catch {
    /* never breaks the app */
  }
}

/** The account id only; null on sign-out. */
export function identifyPostHog(userId: string | null): void {
  pendingId = userId;
  try {
    if (!ph) return;
    if (userId) ph.identify(userId);
    else ph.reset();
  } catch { /* ignore */ }
}

export function capturePostHog(event: string, props?: Record<string, unknown>): void {
  try { ph?.capture(event, props); } catch { /* ignore */ }
}

export function isPostHogOptedOut(): boolean {
  return optedOut();
}

/** Settings switch. Off stops it now and on every later visit on this device. */
export function setPostHogOptOut(out: boolean): void {
  try { localStorage.setItem(POSTHOG_OPT_OUT_KEY, out ? '1' : '0'); } catch { /* ignore */ }
  try {
    if (out) ph?.opt_out_capturing();
    else if (ph) ph.opt_in_capturing();
    else void startPostHog();
  } catch { /* ignore */ }
  if (!out) started = started && !!ph;
}
